import { useState, useEffect, ImgHTMLAttributes } from 'react';
import { mediaService } from '@/services/mediaService';

interface SafeImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  fallbackSrc?: string;
  useFallbackService?: boolean;
}

export function SafeImage({ 
  src, 
  fallbackSrc, 
  useFallbackService = true,
  alt = '',
  onError,
  ...props 
}: SafeImageProps) {
  const [imgSrc, setImgSrc] = useState(() => {
    if (useFallbackService) {
      const resolved = mediaService.resolveMediaWithFallback(src);
      return resolved.primary;
    }
    return src;
  });

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
    if (useFallbackService) {
      const resolved = mediaService.resolveMediaWithFallback(src);
      setImgSrc(resolved.primary);
    } else {
      setImgSrc(src);
    }
  }, [src, useFallbackService]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (!hasError) {
      setHasError(true);
      
      if (useFallbackService) {
        const resolved = mediaService.resolveMediaWithFallback(src);
        if (resolved.fallback && imgSrc !== resolved.fallback) {
          setImgSrc(resolved.fallback);
          return;
        }
      }
      
      if (fallbackSrc && imgSrc !== fallbackSrc) {
        setImgSrc(fallbackSrc);
        return;
      }
    }
    
    if (onError) {
      onError(e);
    }
  };

  return (
    <img 
      src={imgSrc} 
      alt={alt}
      onError={handleError}
      {...props}
    />
  );
}

export default SafeImage;
